// Enhanced hook for using the profile endpoint with staging domain workaround
import { useCallback, useEffect, useState } from 'react';
import { useSupabaseAuth } from '../auth/simplified/SupabaseAuthContext';
import { UserProfile } from '@/types';

/**
 * Enhanced profile fetching hook that handles staging domain issues
 * Uses the centralized /api/auth/profile endpoint instead of direct database access
 */
export function useProfileFetch() {
  const { user, session, refreshProfile } = useSupabaseAuth();
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  
  // Function to extract access token directly from session
  const getAccessToken = useCallback(() => {
    // With simplified auth, we can get the token directly from the session
    if (session?.access_token) {
      return session.access_token;
    }
    return null;
  }, [session]);
  
  // Set up special handling for staging domain
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Only apply fix for staging domain
    if (!window.location.hostname.includes('staging.apprendo.co')) return;
    
    // Access URL hash to see if we're in an auth flow
    // The URL hash may contain access_token and/or id_token
    const checkHash = () => {
      if (window.location.hash && (
          window.location.hash.includes('access_token=') || 
          window.location.hash.includes('id_token=')
      )) {
        console.log('[useProfileFetch] Detected auth tokens in URL hash, saving');
        // We need to extract all tokens and save them
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        
        const accessToken = params.get('access_token');
        // Validate token format before proceeding
        if (!accessToken || !accessToken.startsWith('eyJ') || accessToken.split('.').length !== 3) {
          console.warn('[useProfileFetch] Invalid access token format in URL hash');
        } else {
          // Build a fake session for storage
          const session = {
            access_token: accessToken,
            refresh_token: params.get('refresh_token') || '',
            expires_in: parseInt(params.get('expires_in') || '3600', 10),
            token_type: params.get('token_type') || 'bearer',
            user: { id: 'pending' }
          };
          
          // Save to localStorage
          const cookieName = 'sb-lpjvndsvkvqgkdkvacnn-auth-token';
          localStorage.setItem(cookieName, JSON.stringify(session));
          console.log('[useProfileFetch] Saved session from URL hash to localStorage');
          
          // Clear the hash to avoid processing again
          if (window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
          
          return true;
        }
      }
      return false;
    };
    
    // Check hash first - this handles redirects from Supabase auth
    const hasHash = checkHash();
    
    // Store user ID in localStorage as a backup whenever we have it
    if (user?.id) {
      console.log('[useProfileFetch] Storing user ID in localStorage for staging domain:', user.id);
      localStorage.setItem('staging_auth_user_id', user.id);
      setActiveUserId(user.id);
    } else {
      // Try to get from localStorage if not in context
      const storedId = localStorage.getItem('staging_auth_user_id');
      if (storedId) {
        console.log('[useProfileFetch] Found user ID in localStorage:', storedId);
        setActiveUserId(storedId);
      }
    }
    
    // Detect session changes via global event listener
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'sb-lpjvndsvkvqgkdkvacnn-auth-token') {
        console.log('[useProfileFetch] Auth storage changed, checking for user ID');
        try {
          if (e.newValue) {
            const data = JSON.parse(e.newValue);
            if (data?.user?.id && data.user.id !== 'pending') {
              console.log('[useProfileFetch] Found user ID in storage event:', data.user.id);
              localStorage.setItem('staging_auth_user_id', data.user.id);
              setActiveUserId(data.user.id);
            }
          }
        } catch (e) {
          console.error('[useProfileFetch] Error handling storage event:', e);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    
    // Create a global fetch interceptor for all API calls
    const originalFetch = window.fetch;
    window.fetch = async function(input, init) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      // Current best userId - from state, context, or localStorage
      const currentUserId = activeUserId || user?.id || localStorage.getItem('staging_auth_user_id');
      
      // For ALL api calls, not just profile
      if (url.includes('/api/')) {
        // Create new headers object to avoid modifying original
        const newInit = { ...init, headers: { ...(init?.headers || {}) } };
        
        // Add auth headers if we have the data
        if (currentUserId) {
          newInit.headers = {
            ...newInit.headers,
            'x-user-id': currentUserId
          };
        }
        
        // Add access token if available
        const accessToken = getAccessToken();
        if (accessToken) {
          newInit.headers = {
            ...newInit.headers,
            'Authorization': `Bearer ${accessToken}`
          };
        }
        
        // Add extra headers for staging domain
        newInit.headers = {
          ...newInit.headers,
          'x-staging-fallback': 'true'
        };
        
        // Only log for profile endpoint
        if (url.includes('/api/auth/profile')) {
          console.log('[useProfileFetch] Enhanced profile API call with headers:', 
            Object.keys(newInit.headers).join(', '));
        }
        
        return originalFetch(input, newInit);
      }
      
      // For all other requests, use original fetch
      return originalFetch(input, init);
    };
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.fetch = originalFetch;
    };
  }, [user, session, activeUserId, getAccessToken]);
  
  // Add a function to fetch profile explicitly
  const fetchProfile = useCallback(async () => {
    if (typeof window === 'undefined') return null;
    
    try {
      // Current best userId - from state, context, or localStorage
      const currentUserId = activeUserId || user?.id || localStorage.getItem('staging_auth_user_id');
      const accessToken = getAccessToken();
      
      const headers: Record<string, string> = {
        // Always include these headers
        'x-staging-fallback': 'true'
      };
      
      // Add auth headers if we have them
      if (currentUserId) headers['x-user-id'] = currentUserId;
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      
      console.log('[useProfileFetch] Explicitly fetching profile with headers:', 
        Object.keys(headers).join(', '));
      
      const response = await fetch('/api/auth/profile', { 
        headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('[useProfileFetch] Profile fetch failed:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      // If we got a successful response with a user ID, store it
      if (data?.authUser?.id) {
        localStorage.setItem('staging_auth_user_id', data.authUser.id);
        setActiveUserId(data.authUser.id);
        
        // We might also want to update the session in localStorage
        try {
          const cookieName = 'sb-lpjvndsvkvqgkdkvacnn-auth-token';
          const rawData = localStorage.getItem(cookieName);
          if (rawData) {
            try {
              const parsedSession = JSON.parse(rawData);
              if (parsedSession.user?.id === 'pending' || !parsedSession.user) {
                parsedSession.user = data.authUser;
                localStorage.setItem(cookieName, JSON.stringify(parsedSession));
                console.log('[useProfileFetch] Updated session in localStorage with real user data');
              }
            } catch (e) {
              console.error('[useProfileFetch] Error updating session in localStorage:', e);
            }
          }
        } catch (e) {
          console.error('[useProfileFetch] Error handling session update:', e);
        }
        
        console.log('[useProfileFetch] Received profile data successfully for user:', data.authUser.id);
      } else {
        console.log('[useProfileFetch] Received profile data but no user ID found');
      }
      
      return data;
    } catch (e) {
      console.error('[useProfileFetch] Error fetching profile:', e);
      return null;
    }
  }, [user, activeUserId, getAccessToken]);
  
  // Expose functions and state
  return { 
    fetchProfile,
    activeUserId,
    hasLocalStorageSession: !!localStorage.getItem('sb-lpjvndsvkvqgkdkvacnn-auth-token')
  };
}